/**
 * Page calculation utilities for Table of Contents generation
 */

export interface PageInfo {
  title: string;
  startPage: number;
  wordCount: number;
}

export interface BookPageStructure {
  frontMatter: PageInfo[];
  chapters: PageInfo[];
  backMatter: PageInfo[];
  totalPages: number;
}

/**
 * Configuration for page calculation
 */
const PAGE_CONFIG = {
  WORDS_PER_PAGE: 275, // Average words per page for a standard book
  FRONT_MATTER_PAGES: {
    titlePage: 1,
    copyrightPage: 1, 
    dedication: 1,
    epigraph: 1,
    tableOfContents: 1, // Will be recalculated based on content
    foreword: 2, // Average pages
    preface: 2,
    acknowledgments: 1,
    prologue: 3, // Average pages
    introduction: 2
  },
  BACK_MATTER_PAGES: {
    epilogue: 3,
    afterword: 2,
    authorsNote: 1,
    acknowledgments: 1,
    appendices: 2, // Per appendix
    glossary: 2,
    bibliography: 2,
    index: 3,
    readingGroupGuide: 2,
    teaserNextBook: 3,
    aboutTheAuthor: 1,
    contactSocialMedia: 1
  }
};

/**
 * Count words in a text string
 */
export function countWords(text: string): number {
  if (!text || typeof text !== 'string') return 0;
  
  // Remove HTML tags and clean the text
  const cleanText = text
    .replace(/<\/?[^>]+>/g, '') // Remove HTML tags
    .replace(/```[\s\S]*?```/g, '') // Remove code blocks
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim();
    
  if (!cleanText) return 0;
  
  // Split by whitespace and filter out empty strings
  return cleanText.split(/\s+/).filter(word => word.length > 0).length;
}

/**
 * Calculate pages needed for given word count
 */
export function calculatePages(wordCount: number): number {
  return Math.max(1, Math.ceil(wordCount / PAGE_CONFIG.WORDS_PER_PAGE));
}

/**
 * Calculate the complete page structure for a book
 */
export function calculateBookPageStructure(projectState: any): BookPageStructure {
  let currentPage = 1;
  const frontMatter: PageInfo[] = [];
  const chapters: PageInfo[] = [];
  const backMatter: PageInfo[] = [];

  // Calculate front matter pages
  const fm = projectState.frontMatter || {};
  
  if (fm.titlePageContent || projectState.worldBuildingData?.selectedStoryTitle) {
    frontMatter.push({
      title: 'Title Page',
      startPage: currentPage,
      wordCount: 0
    });
    currentPage += PAGE_CONFIG.FRONT_MATTER_PAGES.titlePage;
  }

  if (fm.copyrightPageContent) {
    frontMatter.push({
      title: 'Copyright',
      startPage: currentPage,
      wordCount: countWords(fm.copyrightPageContent)
    });
    currentPage += PAGE_CONFIG.FRONT_MATTER_PAGES.copyrightPage;
  }

  if (fm.dedication) {
    frontMatter.push({
      title: 'Dedication',
      startPage: currentPage,
      wordCount: countWords(fm.dedication)
    });
    currentPage += PAGE_CONFIG.FRONT_MATTER_PAGES.dedication;
  }

  if (fm.epigraph) {
    frontMatter.push({
      title: 'Epigraph',
      startPage: currentPage,
      wordCount: countWords(fm.epigraph)
    });
    currentPage += PAGE_CONFIG.FRONT_MATTER_PAGES.epigraph;
  }

  // Table of Contents placeholder - will be calculated dynamically
  const tocPage = currentPage;
  if (fm.tableOfContents && fm.tableOfContents !== '<!-- Table of Contents will be generated here -->') {
    frontMatter.push({
      title: 'Table of Contents',
      startPage: currentPage,
      wordCount: countWords(fm.tableOfContents)
    });
    currentPage += Math.max(1, calculatePages(countWords(fm.tableOfContents)));
  } else {
    // Reserve space for ToC
    frontMatter.push({
      title: 'Table of Contents',
      startPage: currentPage,
      wordCount: 0
    });
    currentPage += PAGE_CONFIG.FRONT_MATTER_PAGES.tableOfContents;
  }

  if (fm.foreword) {
    frontMatter.push({
      title: 'Foreword',
      startPage: currentPage,
      wordCount: countWords(fm.foreword)
    });
    currentPage += Math.max(PAGE_CONFIG.FRONT_MATTER_PAGES.foreword, calculatePages(countWords(fm.foreword)));
  }

  if (fm.preface) {
    frontMatter.push({
      title: 'Preface',
      startPage: currentPage,
      wordCount: countWords(fm.preface)
    });
    currentPage += Math.max(PAGE_CONFIG.FRONT_MATTER_PAGES.preface, calculatePages(countWords(fm.preface)));
  }

  if (fm.acknowledgmentsFront) {
    frontMatter.push({
      title: 'Acknowledgments',
      startPage: currentPage,
      wordCount: countWords(fm.acknowledgmentsFront)
    });
    currentPage += Math.max(PAGE_CONFIG.FRONT_MATTER_PAGES.acknowledgments, calculatePages(countWords(fm.acknowledgmentsFront)));
  }

  if (fm.prologue) {
    frontMatter.push({
      title: 'Prologue',
      startPage: currentPage,
      wordCount: countWords(fm.prologue)
    });
    currentPage += Math.max(PAGE_CONFIG.FRONT_MATTER_PAGES.prologue, calculatePages(countWords(fm.prologue)));
  }

  if (fm.introduction) {
    frontMatter.push({
      title: 'Introduction',
      startPage: currentPage,
      wordCount: countWords(fm.introduction)
    });
    currentPage += Math.max(PAGE_CONFIG.FRONT_MATTER_PAGES.introduction, calculatePages(countWords(fm.introduction)));
  }

  // Calculate chapter pages
  if (projectState.chapters && Array.isArray(projectState.chapters)) {
    projectState.chapters.forEach((chapter: any, index: number) => {
      const content = chapter.previewContent || '';
      const wordCount = countWords(content);
      const pagesNeeded = calculatePages(wordCount);

      chapters.push({
        title: `Chapter ${index + 1}: ${chapter.title || 'Untitled'}`,
        startPage: currentPage,
        wordCount
      });

      currentPage += pagesNeeded;
    });
  }

  // Calculate back matter pages
  const bm = projectState.backMatter || {};

  if (bm.epilogue) {
    backMatter.push({
      title: 'Epilogue',
      startPage: currentPage,
      wordCount: countWords(bm.epilogue)
    });
    currentPage += Math.max(PAGE_CONFIG.BACK_MATTER_PAGES.epilogue, calculatePages(countWords(bm.epilogue)));
  }

  if (bm.afterword) {
    backMatter.push({
      title: 'Afterword',
      startPage: currentPage,
      wordCount: countWords(bm.afterword)
    });
    currentPage += Math.max(PAGE_CONFIG.BACK_MATTER_PAGES.afterword, calculatePages(countWords(bm.afterword)));
  }

  if (bm.authorsNote) {
    backMatter.push({
      title: "Author's Note",
      startPage: currentPage,
      wordCount: countWords(bm.authorsNote)
    });
    currentPage += Math.max(PAGE_CONFIG.BACK_MATTER_PAGES.authorsNote, calculatePages(countWords(bm.authorsNote)));
  }

  if (bm.acknowledgmentsBack) {
    backMatter.push({
      title: 'Acknowledgments',
      startPage: currentPage,
      wordCount: countWords(bm.acknowledgmentsBack)
    });
    currentPage += Math.max(PAGE_CONFIG.BACK_MATTER_PAGES.acknowledgments, calculatePages(countWords(bm.acknowledgmentsBack)));
  }

  if (bm.appendices && Array.isArray(bm.appendices) && bm.appendices.length > 0) {
    bm.appendices.forEach((appendix: any, index: number) => {
      backMatter.push({
        title: `Appendix ${String.fromCharCode(65 + index)}: ${appendix.title}`,
        startPage: currentPage,
        wordCount: countWords(appendix.content)
      });
      currentPage += Math.max(PAGE_CONFIG.BACK_MATTER_PAGES.appendices, calculatePages(countWords(appendix.content)));
    });
  }

  if (bm.glossary && Array.isArray(bm.glossary) && bm.glossary.length > 0) {
    const glossaryText = bm.glossary.map((g: any) => `${g.term}: ${g.definition}`).join('\n');
    backMatter.push({
      title: 'Glossary',
      startPage: currentPage,
      wordCount: countWords(glossaryText)
    });
    currentPage += Math.max(PAGE_CONFIG.BACK_MATTER_PAGES.glossary, calculatePages(countWords(glossaryText)));
  }

  if (bm.bibliography && Array.isArray(bm.bibliography) && bm.bibliography.length > 0) {
    const bibliographyText = bm.bibliography.map((b: any) => b.citation).join('\n');
    backMatter.push({
      title: 'Bibliography',
      startPage: currentPage,
      wordCount: countWords(bibliographyText)
    });
    currentPage += Math.max(PAGE_CONFIG.BACK_MATTER_PAGES.bibliography, calculatePages(countWords(bibliographyText)));
  }

  if (bm.index) {
    backMatter.push({
      title: 'Index',
      startPage: currentPage,
      wordCount: countWords(bm.index)
    });
    currentPage += Math.max(PAGE_CONFIG.BACK_MATTER_PAGES.index, calculatePages(countWords(bm.index)));
  }

  if (bm.readingGroupGuide) {
    backMatter.push({
      title: 'Reading Group Guide',
      startPage: currentPage,
      wordCount: countWords(bm.readingGroupGuide)
    });
    currentPage += Math.max(PAGE_CONFIG.BACK_MATTER_PAGES.readingGroupGuide, calculatePages(countWords(bm.readingGroupGuide)));
  }

  if (bm.teaserNextBook) {
    backMatter.push({
      title: 'Teaser for Next Book',
      startPage: currentPage,
      wordCount: countWords(bm.teaserNextBook)
    });
    currentPage += Math.max(PAGE_CONFIG.BACK_MATTER_PAGES.teaserNextBook, calculatePages(countWords(bm.teaserNextBook)));
  }

  if (bm.aboutTheAuthor || projectState.authorDetails?.authorBio) {
    backMatter.push({
      title: 'About the Author',
      startPage: currentPage,
      wordCount: countWords(bm.aboutTheAuthor || projectState.authorDetails?.authorBio)
    });
    currentPage += PAGE_CONFIG.BACK_MATTER_PAGES.aboutTheAuthor;
  }

  if (bm.contactSocialMedia) {
    backMatter.push({
      title: 'Contact / Social Media',
      startPage: currentPage,
      wordCount: countWords(bm.contactSocialMedia)
    });
    currentPage += PAGE_CONFIG.BACK_MATTER_PAGES.contactSocialMedia;
  }

  return {
    frontMatter,
    chapters,
    backMatter,
    totalPages: currentPage - 1
  };
}

/**
 * Generate a formatted Table of Contents with page numbers
 */
export function generateTableOfContents(pageStructure: BookPageStructure): string {
  let toc = '';

  // Add front matter items (excluding title page and ToC itself)
  const frontMatterItems = pageStructure.frontMatter.filter(
    item => !['Title Page', 'Table of Contents'].includes(item.title)
  );
  
  if (frontMatterItems.length > 0) {
    frontMatterItems.forEach(item => {
      toc += `${item.title} .................................. ${item.startPage}\n`;
    });
    toc += '\n';
  }

  // Add chapters
  if (pageStructure.chapters.length > 0) {
    pageStructure.chapters.forEach(chapter => {
      toc += `${chapter.title} .................................. ${chapter.startPage}\n`;
    });
    toc += '\n';
  }

  // Add back matter items
  if (pageStructure.backMatter.length > 0) {
    pageStructure.backMatter.forEach(item => {
      toc += `${item.title} .................................. ${item.startPage}\n`;
    });
  }

  return toc.trim();
}